/**
 * List sort modes (spec §9 "Sort"): base order is `receivedAt ?? date` desc then uid desc;
 * `unread` / `starred` bring those rows first; `sender` / `subject` use Arabic collation.
 */
import type { MessageRow, SortMode } from '$lib/types';
import { rowTime } from './threads';

export const SORT_MODES: readonly SortMode[] = Object.freeze(['date', 'unread', 'starred', 'sender', 'subject']);

export function isSortMode(v: string | null | undefined): v is SortMode {
  return (SORT_MODES as readonly string[]).includes(v ?? '');
}

const byDate = (a: MessageRow, b: MessageRow) => rowTime(b) - rowTime(a) || b.uid - a.uid;

/** Sorts in place and returns the same array (matches the legacy `sortRows`). */
export function sortRows<T extends MessageRow>(rows: T[], mode: SortMode): T[] {
  switch (mode) {
    case 'unread':
      return rows.sort((a, b) => +!b.flags.includes('\\Seen') - +!a.flags.includes('\\Seen') || byDate(a, b));
    case 'starred':
      return rows.sort((a, b) => Number(b.flags.includes('\\Flagged')) - Number(a.flags.includes('\\Flagged')) || byDate(a, b));
    case 'sender':
      return rows.sort((a, b) => (a.fromName || a.fromAddr).localeCompare(b.fromName || b.fromAddr, 'ar') || byDate(a, b));
    case 'subject':
      return rows.sort((a, b) => (a.subject || '').localeCompare(b.subject || '', 'ar') || byDate(a, b));
    default:
      return rows.sort(byDate);
  }
}
